import { create } from '@bufbuild/protobuf'
import { createClient } from '@connectrpc/connect'
import { createConnectQueryKey, useQuery } from '@connectrpc/connect-query'
import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'

import { getAlert, listAlerts } from '@/gen/inventory/v1/alert_service-AlertService_connectquery'
import { AlertService, ListAlertsRequest } from '@/gen/inventory/v1/alert_service_pb'
import { GetAlertResponseSchema } from '@/gen/inventory/v1/alert_service_pb'

import { defaultTransport } from '@/lib/connect/connect-transport'

export const getGetAlertQueryKey = () => {
  return createConnectQueryKey({
    schema: AlertService.method.getAlert,
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const getListAlertsQueryKey = () => {
  return createConnectQueryKey({
    schema: AlertService.method.listAlerts,
    cardinality: 'finite',
    transport: defaultTransport,
  })
}

export const useGetAlert = (id?: string) => {
  return useQuery(getAlert, { id: id ?? '' }, { enabled: !!id })
}

export const useListAlerts = (req?: ListAlertsRequest, opts?: { enabled?: boolean }) => {
  const res = useQuery(listAlerts, req ?? {}, { enabled: opts?.enabled ?? true })

  return {
    ...res,
    data: {
      ...res.data,
      alerts: res.data?.alerts ?? [],
    },
  }
}

/** Badge figure without the row payload; shares the listAlerts key so mutations invalidate it. */
export const useListAlertsCounts = (
  req?: Pick<ListAlertsRequest, 'companyId' | 'severities' | 'workStatuses'>,
  opts?: { enabled?: boolean },
) => {
  return useQuery(listAlerts, { ...req, countOnly: true }, { enabled: opts?.enabled ?? true })
}

export const useGetAlertsWithMarkdown = ({
  ids,
  enabled = true,
}: {
  ids: string[]
  enabled?: boolean
}) => {
  const client = createClient(AlertService, defaultTransport)
  const validIds = useMemo(() => ids.filter((id) => id), [ids])

  const queries = useQueries({
    queries: validIds.map((id) => ({
      queryKey: createConnectQueryKey({
        schema: AlertService.method.getAlert,
        cardinality: 'finite',
        transport: defaultTransport,
        input: { id },
      }),
      queryFn: () => client.getAlert({ id }),
      enabled,
    })),
  })

  return {
    isPending: queries.some((query) => query.isPending),
    isError: queries.some((query) => query.isError),
    data: queries.map((query) => create(GetAlertResponseSchema, query.data)),
    queries,
  }
}
